import { Card, Select } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import useReportLog from '../../../../hooks/useReportLog';
import styled, { css } from 'styled-components';
import {
  calculateGpsSummary,
  CLUTCH_OPERATION_MODE_KEYS,
  generateReportOperationModeSummary,
  generateReportSummary,
  generateTimestampData,
  getPresentOperationModeKeys,
  toHHMMSS,
} from '../helperFunction';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import { EAssetType } from '../../../asset/types/asset.type';
import { EEmailBodyParams } from '../../../masterReport/types/report.type';
import { EReportDataInterval } from '../../types/report-log.type';
import {
  cruiseUnitOptions,
  distanceUnitOptions,
  speedUnitOptions,
} from '../../../../helpers/unit';
import { httpRequest } from '../../../../helpers/api';
import {
  BaseResponsePaginationProps,
  capitalizeFirstLetter,
  generateQueryString,
} from '@qlibs/react-components';
import {
  isClutchOperationModeDevice,
  OperationModeDetailProperties,
  OperationModeProperties,
} from '../../../operationMode/types/operationMode.type';

interface IProps {
  engine?: 'port' | 'starboard';
  source?: 'engine' | 'clutch';
  isReportSchedule?: boolean;
  interval?: string;
  startAt?: number;
  endAt?: number;
}

const SummaryOperationModeTable: React.FC<IProps> = (props) => {
  const source = props.source || 'engine';
  const { logs } = useReportLog();
  const { filter, timezone } = useAppliedReportFilter();
  const [loading, setLoading] = useState<boolean>(false);
  const [opModes, setOpModes] = useState<OperationModeDetailProperties[]>([]);

  useEffect(() => {
    let ignore = false;

    if (!logs?.operation_mode || !Object.keys(logs.operation_mode).length) {
      setOpModes([]);
      setLoading(false);
      return () => {
        ignore = true;
      };
    }

    async function fetchOperationMode() {
      try {
        setLoading(true);
        setOpModes([]);
        const positionKeys =
          source === 'clutch'
            ? [...CLUTCH_OPERATION_MODE_KEYS]
            : ['port', 'starboard'];
        const devicesIds = new Set<string>();
        Object.values(logs.operation_mode!).forEach((bucket) => {
          positionKeys.forEach((key) => {
            const deviceUniqueId = bucket?.[key]?.deviceUniqueId;
            if (deviceUniqueId) devicesIds.add(deviceUniqueId);
          });
        });

        if (!devicesIds.size) {
          return;
        }

        const res = await httpRequest.get<
          BaseResponsePaginationProps<OperationModeProperties>
        >(
          `/operation-mode/data-constant/by-devices${generateQueryString({
            filterDevicesIds: Array.from(devicesIds),
            limit: 20,
          })}`,
        );

        if (!ignore) {
          const result: OperationModeDetailProperties[] = [];
          res.data.payload.results?.forEach((item) => {
            const isClutchDevice = isClutchOperationModeDevice(item.deviceId);
            if (source === 'clutch' ? !isClutchDevice : isClutchDevice) return;
            item.operationModeDetails?.forEach((detail) => {
              result.push(detail);
            });
          });
          setOpModes(result);
        }
      } catch (error) {
        if (!ignore) {
          setOpModes([]);
          console.error('Failed get operation mode details', error);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    fetchOperationMode();

    return () => {
      ignore = true;
    };
  }, [logs, source]);

  const dataSummaryOp = useMemo(() => {
    let result: any[] = [];
    let totalRunningTime: any = 0;
    let totalConsRate: any = 0;

    const clutchKeys =
      source === 'clutch'
        ? getPresentOperationModeKeys(logs?.operation_mode, CLUTCH_OPERATION_MODE_KEYS)
        : undefined;

    const canSummarize =
      opModes.length > 0 &&
      (source === 'clutch' ? Boolean(clutchKeys?.length) : true);

    if (canSummarize) {
      result = generateReportOperationModeSummary({
        startAt:
          props.isReportSchedule && props.startAt
            ? props.startAt
            : filter.startAt,
        endAt:
          props.isReportSchedule && props.endAt ? props.endAt : filter.endAt,
        interval:
          props.isReportSchedule && props.interval
            ? props.interval
            : filter.interval,
        logs,
        opModes,
        timezone,
        engine: props.engine,
        engines: clutchKeys,
      });

      totalRunningTime = result.reduce(
        (acc, curr) => acc + curr.runningTime,
        0
      );

      totalConsRate = result.reduce((acc, curr) => acc + curr.consRate, 0);
    }

    return {
      result,
      totalRunningTime,
      totalConsRate,
    };
  }, [
    filter.endAt,
    filter.interval,
    filter.startAt,
    logs,
    opModes,
    props.endAt,
    props.engine,
    props.interval,
    props.isReportSchedule,
    props.startAt,
    source,
    timezone,
  ]);

  const isCompact = Boolean(props.engine);
  const totalRow = (
    <Tr titleSection $compact={isCompact}>
      <Td>Total</Td>
      <Td>{toHHMMSS(dataSummaryOp.totalRunningTime)}</Td>
      <Td>{dataSummaryOp.totalConsRate.toFixed(2)}</Td>
    </Tr>
  );

  return (
    <Wrapper $compact={isCompact}>
      {loading ? (
        <Table2 $compact={isCompact}>
          <tbody>
            <Tr>
              <Td colSpan={3}>Loading data</Td>
            </Tr>
          </tbody>
        </Table2>
      ) : (
        <Table2 $compact={isCompact}>
          <thead>
            <Tr titleSection $compact={isCompact}>
              <Td>
                {source === 'clutch' ? 'Operation Mode Clutch' : 'Operation Mode'}
              </Td>
              <Td>Running Time</Td>
              <Td>{props.engine ? 'Fuel Cons (Liter)' : 'Fuel Cons.'}</Td>
            </Tr>
          </thead>
          <tbody>
            {dataSummaryOp.result.map((item, idx) => (
              <Tr key={idx}>
                <Td>
                  {props.engine
                    ? capitalizeFirstLetter(item.name)
                    : item.name.toUpperCase()}
                </Td>
                <Td>{toHHMMSS(item.runningTime)}</Td>
                <Td>{item.consRate.toFixed(2)}</Td>
              </Tr>
            ))}
            {totalRow}
          </tbody>
        </Table2>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ column?: number; $compact?: boolean }>`
  display: flex;
  flex-direction: column;
  column-gap: 20px;
  grid-template-columns: ${({ column: gridColumn }) =>
    gridColumn ? `repeat(0,${gridColumn}fr)` : 'repeat(0, 2fr)'};

  ${({ $compact }) =>
    $compact &&
    css`
      @media (min-width: 768px) {
        flex: 1 1 auto;
        min-height: 0;
      }
    `}
`;

const Table2 = styled.table<{ $compact?: boolean }>`
  margin-top: ${({ $compact }) => ($compact ? 0 : '30px')};
  width: 100%;

  ${({ $compact }) =>
    $compact &&
    css`
      display: inline-table;

      td {
        height: 32px !important;
      }

      @media (min-width: 768px) {
        display: table;
        flex: 1 1 auto;
        height: 100%;
        min-height: 0;
        table-layout: fixed;
      }

      @media (max-width: 767px) {
        display: table;
        table-layout: fixed;
        border-collapse: collapse;
        margin-top: 8px;
        background: #fff;
        font-size: 14px;
        line-height: 1.2;

        td {
          height: 42px !important;
          padding: 0 8px;
          border-top: 0;
          border-bottom: 1px solid #d9d9d9;
          color: #1e1e1e;
          font-size: 14px;
          vertical-align: middle;
          white-space: normal;
        }

        td:first-child {
          width: 44%;
          padding-left: 12px;
        }

        td:nth-child(2) {
          width: 24%;
          text-align: center;
          font-weight: 600;
        }

        td:last-child {
          width: 32%;
          padding-right: 12px;
        }

        td[colspan] {
          width: auto;
          height: 42px !important;
          padding: 0 12px;
          text-align: left;
          font-weight: 600;
        }
      }
    `}

  thead td,
  tbody td {
    text-align: center;
  }
`;

const Tr = styled.tr<{ titleSection?: boolean; $compact?: boolean }>`
  background-color: ${({ titleSection, $compact }) =>
    titleSection ? ($compact ? 'var(--gray-0)' : 'rgb(221, 221, 221)') : 'white'};
  font-weight: ${({ titleSection }) => (titleSection ? 'bold' : 'normal')};

  ${({ $compact, titleSection }) =>
    $compact &&
    css`
      @media (max-width: 767px) {
        background-color: ${titleSection ? '#e8ebf3' : '#fff'};
        font-weight: ${titleSection ? 600 : 400};
      }
    `}
`;

const Td = styled.td<{ unit?: boolean; value?: boolean }>`
  border-top: thin solid #ddd;
  border-bottom: thin solid #ddd;
  width: 10%;
  padding: 4px 8px;
  font-weight: ${({ value }) => (value ? 'bold' : 'regular')};
  color: ${({ unit }) => (unit ? 'gray' : 'black')};
`;

const CardInfo = styled(Card)`
  background: var(--gray-2);
  .ant-card-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 10px;

    div {
      :first-child {
        opacity: 0.7;
      }
      :last-child {
        font-weight: bold;
      }
    }
  }
  .ant-card-body::before,
  .ant-card-body::after {
    display: none;
  }
`;

const Accordion = styled.div`
  margin-top: 15px;
  grid-column: 4;
`;

const CardInfoTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-top: 10px;
`;

const CardInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr) 40px;
  gap: 20px;
`;

const CardInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Value = styled.div`
  font-size: 14px;
`;

const Label = styled.div`
  font-size: 12px;
  opacity: 0.5;
`;

export default SummaryOperationModeTable;
